import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ActiveJob } from '../api';
import AIRunsLogModal from './AIRunsLogModal';
import { getJobUrl } from '../utils/jobLinks';
import './AIActivityIndicator.css';

// ─── Action → human-friendly label ───────────────────────────────────────────

const ACTION_LABELS: Record<string, string> = {
  ACTIVATION: 'Setting up workspace',
  GENERATE_IMAGES: 'Generating images',
  GENERATE_VIDEOS: 'Creating videos',
  CREATE_OUTLINES: 'Planning content',
  CREATE_CONTENT: 'Writing content',
  REGENERATE_CONTENT: 'Rewriting content',
  SUPERVISOR: 'Reviewing pipeline',
  BRAIN: 'Thinking',
  CHAT: 'Responding',
  // Fallback for any new action types
};

function getLabel(action: string): string {
  return ACTION_LABELS[action] ?? action.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

// ─── Orb / pulsing dot ───────────────────────────────────────────────────────

function AiOrb() {
  return (
    <span className="ai-orb" aria-hidden="true">
      <span className="ai-orb__core" />
      <span className="ai-orb__ring" />
    </span>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface Props {
  jobs: ActiveJob[];
  workspaceId: number;
}

export default function AIActivityIndicator({ jobs, workspaceId }: Props) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [visible, setVisible] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const wasActiveRef = useRef(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isActive = jobs.length > 0;
  const primaryJob = jobs[0];

  // Appear / disappear with a short grace period so the "done" state can flash.
  useEffect(() => {
    if (isActive) {
      wasActiveRef.current = true;
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      setVisible(true);
    } else if (wasActiveRef.current) {
      // Keep visible briefly so user sees it disappear gracefully.
      hideTimerRef.current = setTimeout(() => {
        setVisible(false);
        wasActiveRef.current = false;
        setExpanded(false);
      }, 2200);
    }
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [isActive]);

  if (!visible) return null;

  const justFinished = !isActive && wasActiveRef.current;

  /** Handle click on the collapsed pill. */
  function handlePillClick() {
    if (!isActive) {
      // Done state → open log
      setLogOpen(true);
      return;
    }
    if (jobs.length === 1) {
      const url = getJobUrl(primaryJob);
      if (url) {
        navigate(url);
        return;
      }
    }
    if (jobs.length > 1) {
      setExpanded((e) => !e);
    }
  }

  /** Whether the pill is interactive (clickable). */
  const pillClickable =
    !isActive || // done state → opens log
    (jobs.length === 1 && Boolean(getJobUrl(primaryJob))) || // single navigable job
    jobs.length > 1; // multiple jobs → expand

  return (
    <>
      <div
        className={[
          'ai-activity',
          isActive ? 'ai-activity--active' : 'ai-activity--done',
          expanded ? 'ai-activity--expanded' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        role="status"
        aria-live="polite"
        aria-label={isActive ? `AI is working: ${jobs.map((j) => getLabel(j.action)).join(', ')}` : 'AI finished'}
      >
        {/* ── Collapsed pill ─────────────────────────────────────────────── */}
        <button
          className="ai-activity__pill"
          onClick={handlePillClick}
          aria-expanded={isActive && jobs.length > 1 ? expanded : undefined}
          disabled={!pillClickable}
          tabIndex={0}
          title={!isActive ? 'View AI run history' : undefined}
        >
          {isActive ? (
            <>
              <AiOrb />
              <span className="ai-activity__label">
                {getLabel(primaryJob.action)}
                {jobs.length > 1 && (
                  <span className="ai-activity__count"> +{jobs.length - 1}</span>
                )}
              </span>
              {jobs.length > 1 && (
                <span className="ai-activity__chevron" aria-hidden="true">
                  {expanded ? '▲' : '▼'}
                </span>
              )}
              {jobs.length === 1 && getJobUrl(primaryJob) && (
                <span className="ai-activity__link-icon" aria-hidden="true">→</span>
              )}
            </>
          ) : (
            <>
              <span className="ai-activity__done-icon" aria-hidden="true">✓</span>
              <span className="ai-activity__label">{justFinished ? 'Done' : 'AI ready'}</span>
              <span className="ai-activity__link-icon" aria-hidden="true" title="View history">⊙</span>
            </>
          )}
        </button>

        {/* ── Expanded job list ───────────────────────────────────────────── */}
        {expanded && isActive && jobs.length > 1 && (
          <ul className="ai-activity__list">
            {jobs.map((job) => {
              const url = getJobUrl(job);
              function handleClick() { if (url) navigate(url); }
              function handleKeyDown(e: React.KeyboardEvent) {
                if (url && (e.key === 'Enter' || e.key === ' ')) navigate(url);
              }
              return (
                <li
                  key={job.job_id}
                  className={`ai-activity__list-item${url ? ' ai-activity__list-item--link' : ''}`}
                  onClick={url ? handleClick : undefined}
                  role={url ? 'button' : undefined}
                  tabIndex={url ? 0 : undefined}
                  onKeyDown={url ? handleKeyDown : undefined}
                >
                  <AiOrb />
                  <span>{getLabel(job.action)}</span>
                  <span className="ai-activity__status-badge">{job.status}</span>
                  {url && <span className="ai-activity__link-icon" aria-hidden="true">→</span>}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* ── AI Runs Log Modal ───────────────────────────────────────────────── */}
      {logOpen && (
        <AIRunsLogModal workspaceId={workspaceId} onClose={() => setLogOpen(false)} />
      )}
    </>
  );
}

